import type { ContextVariables, FetchDataArguments } from '../types/api';
import { SERVER_ERROR } from '@/constants/text/english';

export const HOST = '/api';

const SESSION_REGENERATED_HEADER = 'X-Session-Regenerated';
const SESSION_REGENERATION_RELOAD_KEY = 'mercury:session-regeneration-reloaded-at';

let sessionRegenerationPending = false;

export const isSessionRegenerationPending = () => sessionRegenerationPending;

const hasSessionRegenerationReloaded = () => {
  try {
    return window.sessionStorage.getItem(SESSION_REGENERATION_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
};

const markSessionRegenerationReloaded = () => {
  try {
    window.sessionStorage.setItem(SESSION_REGENERATION_RELOAD_KEY, Date.now().toString());
  } catch {
    // The in-memory guard still protects the current document.
  }
};

const clearSessionRegenerationReload = () => {
  try {
    window.sessionStorage.removeItem(SESSION_REGENERATION_RELOAD_KEY);
  } catch {
    // Storage availability does not affect ordinary response handling.
  }
};

export const inspectSessionRegeneration = (response: Response): void => {
  if (response.headers?.get?.(SESSION_REGENERATED_HEADER) !== 'true') {
    clearSessionRegenerationReload();
    return;
  }

  if (sessionRegenerationPending || hasSessionRegenerationReloaded()) return;

  markSessionRegenerationReloaded();
  sessionRegenerationPending = true;
  window.location.reload();
};

const isApiGatewayUrl = (input: RequestInfo | URL) => {
  if (typeof window === 'undefined') return false;

  try {
    const value =
      typeof Request !== 'undefined' && input instanceof Request ? input.url : input.toString();
    const url = new URL(value, window.location.href);

    return (
      url.origin === window.location.origin &&
      (url.pathname === HOST || url.pathname.startsWith(`${HOST}/`))
    );
  } catch {
    return false;
  }
};

export const appFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response = await fetch(input, init);

  if (isApiGatewayUrl(input)) {
    inspectSessionRegeneration(response);
  }

  return response;
};

class FetchError extends Error {
  constructor(
    headers: Headers,
    { error: { message, code, details } }: any = {},
    errorMessage?: string
  ) {
    super(errorMessage || message);
    Object.assign(this, { code, details, headers, responseError: message });
  }
}

const getBuiltError = (code: number, message: string) => ({ error: { code, message } });

const addContext = (endpoint: string, context?: ContextVariables) => {
  if (!context || !context.source) return endpoint;

  const [url, queryParams] = endpoint.split('?');

  const urlSearchParams = new URLSearchParams(queryParams);

  urlSearchParams.set('ctx_source', context.source);

  return `${url}?${urlSearchParams.toString()}`;
};

export const fetchData = async (
  {
    endpoint,
    options = { headers: {} },
    error = SERVER_ERROR,
    noContent = 'No data available',
    unauthorized = SERVER_ERROR,
    requestLimit = SERVER_ERROR,
    resolveOnNoContent = false,
    useCompleteEndpoint,
    context,
  }: FetchDataArguments,
  signal?: AbortSignal
) => {
  const url = !useCompleteEndpoint ? `${HOST}/${endpoint}` : endpoint;
  const endpointWithContext = addContext(url, context);
  const response = await appFetch(endpointWithContext, {
    credentials: 'include',
    signal,
    ...options,
  });

  let json;

  if (response.status >= 500) {
    throw new FetchError(response.headers, getBuiltError(response.status, SERVER_ERROR));
  }

  if (response.status === 401) {
    throw new FetchError(response.headers, getBuiltError(response.status, unauthorized));
  }

  if (response.status === 429) {
    throw new FetchError(response.headers, getBuiltError(response.status, requestLimit));
  }

  if (!response.ok) {
    const text = await response.text();

    if (!text) {
      throw new FetchError(response.headers, getBuiltError(response.status, error));
    }

    // Throw a fetch error with an optional custom error.
    throw new FetchError(response.headers, JSON.parse(text), error);
  }

  if (response.status === 204) {
    if (resolveOnNoContent) {
      return response;
    }

    throw new FetchError(response.headers, getBuiltError(response.status, noContent));
  }

  if (!useCompleteEndpoint) {
    try {
      if (response.headers.get('content-type')?.includes('application/json')) {
        json = await response.json();
      } else {
        json = await response.text();
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}

    if (json) {
      return json;
    }
  }

  return response;
};
